import isNil from 'lodash/isNil'
import { ACTIVE_LAYERS_SETTING_ID } from './layerSettings'

/**
 * The user's saved active-layer ids (user setting 1), or [] when the setting has
 * not loaded. Every layer renderer reads the same setting, so they share this.
 */
export const activeLayerIds = userSettings => {
  const setting = (userSettings || []).find(s => s.settingId === ACTIVE_LAYERS_SETTING_ID)
  return Array.isArray(setting?.valueArray) ? setting.valueArray : []
}

/**
 * The layer ids that should actually be DRAWN, given the global eye toggle (WEB-7048).
 *
 * When layers are hidden only the basemap survives — the eye turns off the user's data
 * overlays, not the map underneath them. Two things make up "the basemap": the
 * layerNodeId 5 entry itself, and the companion layer it carries via `overlayLayerId`
 * (hybrid 891 carries topo lines 876). `nextActiveLayerIds` already treats that pair as
 * one unit when swapping basemaps; splitting it here would leave satellite imagery with
 * its contour lines stripped off, which reads as a broken basemap rather than as hidden
 * layers.
 *
 * Nothing here writes to user setting 1 — the selection is untouched and comes straight
 * back when the toggle flips, matching MapController.layersVisible on iOS and Android.
 */
export const visibleLayerIds = ({ activeIds, layersDictionary, layersVisible }) => {
  if (layersVisible) return activeIds
  const dict = layersDictionary || {}
  const basemapId = (activeIds || []).find(id => dict[id]?.layerNodeId === 5)
  if (isNil(basemapId)) return []
  const overlayId = dict[basemapId]?.overlayLayerId ?? null
  return activeIds.filter(id => id === basemapId || id === overlayId)
}

// Stable reference so connect()'s shallow compare doesn't see a new array on every action.
const NO_VENDORS = Object.freeze([])

/**
 * The vendor ids whose map objects should be drawn. Vendor content (markers, lines,
 * photos, tracks, shapes) belongs to vendor data layers, so the eye toggle takes it down
 * with the rest of them — otherwise hiding layers leaves a scatter of vendor pins behind
 * with nothing underneath. Mirrors removeVendorObjects in Android's hideUserLayers.
 *
 * state.activeVendors is returned unchanged when layers are visible, so the identity is
 * stable in both directions.
 */
export const visibleActiveVendors = state =>
  state.layersVisible ? state.activeVendors : NO_VENDORS
